// Module 04 slice 04-B-3 — the FLOOR PRODUCTION seam: everything the S9/S10
// screens need to compose a §5.1 event at TAP TIME and to read the outbox
// honestly, kept out of the screens so the wire shapes are testable as data.
//
// THE SEAM WITH floor-ops.ts / floor-outbox.ts (the settle-fix half of this
// same slice — D-048/D-054: ONE queue, module 06's engine, never a second).
// This file builds the `production_event` operations the screens enqueue;
// floor-ops.ts carries the matching `FloorOp` variant
//
//   { kind: "production_event"; deviceId: string; event: {
//       event_type, worker_id, work_center_id, wo_operation_id?,
//       occurred_at, payload } }
//
// and its `sendFloorOp` case posts `POST /v1/floor/events:batch` with
// `{ device_id: op.deviceId, events: [{ client_event_id: <the idempotency
// key>, ...op.event }] }` — batch-of-1 (D-080: the envelope takes events[] so
// true batching later is transport-only). THE KEY IS THE EVENT ID:
// `clientKey === client_event_id`, minted by the outbox at tap, sent
// unchanged on every attempt; `dedupeKeyOf` returns **undefined** for
// `production_event` (a second tap is a second physical fact — the server's
// `(tenant_id, client_event_id)` UNIQUE is the only dedupe). `asFloorOp`
// below closes the seam statically: a shape drift on either side is a
// compile error.
//
// TAP-TIME FACTS, never re-stamped (§3.4 / §9.6 / D-080):
//   * `occurred_at` — the device wall clock at TAP. The builders take it as an
//     argument so a screen cannot accidentally stamp it at drain time.
//   * `worker_id` — the SESSION principal, echoed by e15 (`snapshot.worker_id`
//     — the 2026-08-05 §5 r15 amendment; D-079 judges every event against it).
//   * `deviceId` — the REGISTERED §2.16 device id (`snapshot.devices` pick,
//     persisted below). e13 404s the whole batch for an unregistered id.

import type { components } from "@assessly/api-client";
import type { FloorOp } from "./floor-ops";
import type { OutboxItem, OutboxSettlement } from "./floor-outbox";
import { looksLikeScanPayload } from "./scan";

type S = components["schemas"];

export type StationSnapshot = S["StationSnapshotResponse"];
export type StationQueueOperation = S["StationQueueOperation"];
export type StationShift = S["StationShift"];
export type StationMaterialExpectation = S["StationMaterialExpectation"];
export type StationSnapshotDevice = S["StationSnapshotDevice"];
export type FloorScrapReason = S["FloorScrapReason"];
export type FloorDowntimeReason = S["FloorDowntimeReason"];
export type FloorOpenDowntime = S["FloorOpenDowntime"];

/**
 * The kinds the pipeline applies (contract `FLOOR_EVENT_KINDS_APPLIED`): 04-B's
 * six, the downtime pair 04-C-1 shipped, `calibration_recorded` (04-C-2),
 * `serial_lot_captured` (04-C-3) and — as of 04-C-5 — `defect_handoff_flagged`.
 * ALL ELEVEN §5.1 kinds now apply, and the client's union says so.
 *
 * THE RULE THIS UNION ENCODES SURVIVES ITS OWN COMPLETION: a kind the server
 * would only PARK stays unrepresentable here, because a screen that can build a
 * tap the pipeline refuses is a screen that teaches an operator their work was
 * saved when it was queued for somebody else's slice (D-033 — absent, not
 * disabled). The list is full today; the rule is what applies to the twelfth.
 */
export type ProductionEventType =
  | "op_started"
  | "op_paused"
  | "op_resumed"
  | "op_completed"
  | "qty_reported"
  | "material_issued"
  | "downtime_started"
  | "downtime_ended"
  | "calibration_recorded"
  | "serial_lot_captured"
  | "defect_handoff_flagged";

export interface ProductionEventBody {
  readonly event_type: ProductionEventType;
  /** MUST equal the session principal (D-079) — from `snapshot.worker_id`. */
  readonly worker_id: string;
  /** The pinned station (§5.1: rides the envelope, from the /q/wc scan). */
  readonly work_center_id: string;
  readonly wo_operation_id?: string;
  /** Device wall clock AT TAP, ISO-8601 — preserved VERBATIM server-side. */
  readonly occurred_at: string;
  readonly payload: Readonly<Record<string, unknown>>;
}

/** The queued-operation shape (see the seam note in the file header). */
export interface ProductionEventOp {
  readonly kind: "production_event";
  readonly deviceId: string;
  readonly event: ProductionEventBody;
}

/**
 * The seam, CLOSED STATICALLY: floor-ops.ts's `FloorOp` union carries the
 * `production_event` variant (the settle-fix half of this slice), and this
 * function is a PLAIN return — no cast — so any drift between the screens'
 * builders and the transport's variant is a compile error, not a runtime
 * surprise. The op-run unit tests additionally pin the exact object that
 * crosses this boundary.
 */
export function asFloorOp(op: ProductionEventOp): FloorOp {
  return op;
}

export interface TapContext {
  readonly deviceId: string;
  readonly workerId: string;
  readonly workCenterId: string;
  /** `Date.now()` at the moment of the TAP. */
  readonly nowMs: number;
}

function buildEvent(
  ctx: TapContext,
  eventType: ProductionEventType,
  woOperationId: string | undefined,
  payload: Readonly<Record<string, unknown>>,
): ProductionEventOp {
  const event: ProductionEventBody = {
    event_type: eventType,
    worker_id: ctx.workerId,
    work_center_id: ctx.workCenterId,
    ...(woOperationId !== undefined ? { wo_operation_id: woOperationId } : {}),
    occurred_at: new Date(ctx.nowMs).toISOString(),
    payload,
  };
  return { kind: "production_event", deviceId: ctx.deviceId, event };
}

/** `op_started {}` — `machine_id` ALWAYS omitted in P2 (D-073: the work
 * centre is the machine grain; there is no equipment table to pick from). */
export function opStartedOp(ctx: TapContext, opId: string): ProductionEventOp {
  return buildEvent(ctx, "op_started", opId, {});
}

/** `op_paused` — a present `downtime_reason_code` LINKS the pause to a §2.7
 * downtime row (live since 04-C-1, D-082: the pause interval IS the downtime,
 * auto-ended by the next resume/complete). The plain Pause button sends no
 * code; the downtime panel's "pause with reason" path sends one. */
export function opPausedOp(
  ctx: TapContext,
  opId: string,
  downtimeReasonCode?: string,
): ProductionEventOp {
  return buildEvent(ctx, "op_paused", opId, {
    ...(downtimeReasonCode !== undefined ? { downtime_reason_code: downtimeReasonCode } : {}),
  });
}

export function opResumedOp(ctx: TapContext, opId: string): ProductionEventOp {
  return buildEvent(ctx, "op_resumed", opId, {});
}

/** `op_completed {}` — qty NEVER rides completion (§5.1); it arrives via
 * prior `qty_reported` events and the §3.2 guard checks one exists. */
export function opCompletedOp(ctx: TapContext, opId: string): ProductionEventOp {
  return buildEvent(ctx, "op_completed", opId, {});
}

export function qtyReportedOp(
  ctx: TapContext,
  opId: string,
  qty: { good: string; scrap: string; scrapReasonCode?: string | undefined },
): ProductionEventOp {
  return buildEvent(ctx, "qty_reported", opId, {
    qty_good: qty.good,
    qty_scrap: qty.scrap,
    ...(qty.scrapReasonCode !== undefined ? { scrap_reason_code: qty.scrapReasonCode } : {}),
  });
}

/** The scan path (`issue_method='scan'` server-side): the RAW scan string on
 * the wire, never a client-resolved id (§5.1). */
export function materialScanOp(
  ctx: TapContext,
  opId: string,
  input: { scanCode: string; qty: string; uom: string },
): ProductionEventOp {
  return buildEvent(ctx, "material_issued", opId, {
    scan_code: input.scanCode,
    qty: input.qty,
    uom: input.uom,
  });
}

/** The no-scan fallback (`issue_method='manual'`): the expected line's
 * component id, exactly one of the two per §5.1. */
export function materialManualOp(
  ctx: TapContext,
  opId: string,
  input: { componentProductId: string; qty: string; uom: string },
): ProductionEventOp {
  return buildEvent(ctx, "material_issued", opId, {
    component_product_id: input.componentProductId,
    qty: input.qty,
    uom: input.uom,
  });
}

/** Normalise an operator-entered magnitude to the NEGATIVE wire qty of a
 * return (D-081): the worker types "40", the wire carries "-40". Exported so
 * the builders and their tests agree on one rule. */
export function negateQty(magnitude: string): string {
  const trimmed = magnitude.trim();
  return trimmed.startsWith("-") ? trimmed : `-${trimmed}`;
}

/**
 * The RETURN, scan arm (D-081, 04-B-4): the same §5.1 `material_issued` row
 * with a NEGATIVE qty — the inverse of the scan issue, on the same screen.
 * The worker enters the MAGNITUDE; the builder owns the sign, so no screen
 * can send a "negative issue" by accident. The server allocates the return
 * newest-first against the WO's unreturned issues (narrowed to the scanned
 * unit), refuses an over-return to the review tray, and costs every slice at
 * its ORIGINAL issue's captured cost (§9.14).
 */
export function materialScanReturnOp(
  ctx: TapContext,
  opId: string,
  input: { scanCode: string; qty: string; uom: string },
): ProductionEventOp {
  return buildEvent(ctx, "material_issued", opId, {
    scan_code: input.scanCode,
    qty: negateQty(input.qty),
    uom: input.uom,
  });
}

/**
 * `downtime_started` (§5.1, 04-C-1) — the S10 downtime button. `opId` binds
 * the interval to the running operation when the worker logs it FROM an op
 * run screen; op-less downtime (§2.3's carve-out — the station itself is
 * down, nothing running) omits it and is equally legal on the wire.
 */
export function downtimeStartedOp(
  ctx: TapContext,
  input: { reasonCode: string; opId?: string | undefined; notes?: string | undefined },
): ProductionEventOp {
  return buildEvent(ctx, "downtime_started", input.opId, {
    downtime_reason_code: input.reasonCode,
    ...(input.notes !== undefined && input.notes !== "" ? { notes: input.notes } : {}),
  });
}

/** `downtime_ended` (§5.1) — references the OPENING tap by its
 * client_event_id (from the e15 snapshot's `open_downtime[]`, or this
 * device's own outbox record). The wire never carries a downtime_log id: the
 * server matches the tap to its open row (§2.7 source_event_id). */
export function downtimeEndedOp(ctx: TapContext, startedClientEventId: string): ProductionEventOp {
  return buildEvent(ctx, "downtime_ended", undefined, {
    started_client_event_id: startedClientEventId,
  });
}

export type CalibrationMethod = "scale_weight" | "meter_counter" | "manual_tape";

/**
 * `calibration_recorded` (§5.1, 04-C-2) — the S10 calibration form on a
 * `requires_measured_capture` step (§3.3 step 7): "Machine says" is the
 * vendor-locked display's CLAIM, "Measured" is scale/meter TRUTH — the only
 * number costing and inventory ever use (D-025). The optional barrel scan is
 * the RAW string (§5.1's scan rule — the server resolves it, and corrects
 * that unit's measure to the measured value through module 06's seam).
 */
export function calibrationRecordedOp(
  ctx: TapContext,
  opId: string,
  input: {
    batchLabel: string;
    machineClaimedQty: string;
    measuredQty: string;
    uom: string;
    method: CalibrationMethod;
    outputScanCode?: string | undefined;
  },
): ProductionEventOp {
  return buildEvent(ctx, "calibration_recorded", opId, {
    batch_label: input.batchLabel,
    machine_claimed_qty: input.machineClaimedQty,
    measured_qty: input.measuredQty,
    uom: input.uom,
    measurement_method: input.method,
    ...(input.outputScanCode !== undefined && input.outputScanCode !== ""
      ? { output_stock_unit_scan_code: input.outputScanCode }
      : {}),
  });
}

export type SerialLotDirection = "consumed" | "produced";

/**
 * `serial_lot_captured` (§5.1, 04-C-3) — the S10 capture on a
 * `requires_serial_capture` step (§3.3 step 8): scan the bobbin's label or type
 * the engraved serial, with the measured length.
 *
 * BOTH identity fields ride when the worker gave both, and that is deliberate:
 * a laser mark and a label barcode are different strings about the same drum,
 * and dropping either loses a way to find it later. The scan is the RAW string
 * (§5.1's rule — the server resolves it, never the client), and the server
 * derives the lot and the product from the unit it resolves.
 */
export function serialLotCapturedOp(
  ctx: TapContext,
  opId: string,
  input: {
    direction: SerialLotDirection;
    serialNo?: string | undefined;
    scanCode?: string | undefined;
    qty: string;
    uom: string;
  },
): ProductionEventOp {
  // A SCANNED LABEL IN THE ENGRAVED-SERIAL BOX IS STILL A SCAN (04-C-3 fix,
  // 2026-08-06). The wedge lands where the thumb was, so the field a payload
  // arrives in is not evidence of what it is — the grammar is. Sent as
  // `serial_no`, a `…/q/su/{uuid}` becomes §2.14's bare-serial case on the
  // server: accepted, unresolvable, `product_id` NULL — a lineage row that
  // looks like a fact and traces to nothing. Promoted here so the QUEUED event
  // is already right, because offline the refusal would otherwise arrive hours
  // later with the drum long gone. The server applies the same rule; this is
  // the mirror, not the guard.
  const rawSerial = input.serialNo ?? "";
  const rawScan = input.scanCode ?? "";
  const promote = rawSerial !== "" && looksLikeScanPayload(rawSerial);
  const serialNo = promote ? "" : rawSerial;
  const scanCode = promote && rawScan === "" ? rawSerial : rawScan;
  return buildEvent(ctx, "serial_lot_captured", opId, {
    direction: input.direction,
    ...(serialNo !== "" ? { serial_no: serialNo } : {}),
    ...(scanCode !== "" ? { scan_code: scanCode } : {}),
    qty: input.qty,
    uom: input.uom,
  });
}

/** The §2.14 at-least-one rule and the positivity CHECK, mirrored client-side
 * so the form refuses before it burns a tap. It is NOT the server's whole
 * guard — an unresolvable scan is still the server's to answer. */
export function serialCaptureProblem(input: {
  serialNo: string;
  scanCode: string;
  qty: string;
  uom: string;
}): "nothing_identified" | "qty_required" | "uom_required" | "scan_in_serial" | undefined {
  if (input.serialNo.trim().length === 0 && input.scanCode.trim().length === 0) {
    return "nothing_identified";
  }
  // TWO LABELS, ONE CAPTURE (04-C-3 fix). A scanned payload in the serial box
  // beside a DIFFERENT scan is the one case the promotion above cannot settle:
  // the form will not pick a drum for the operator, and the server refuses the
  // same shape rather than recording either.
  if (
    input.serialNo.trim().length > 0 &&
    looksLikeScanPayload(input.serialNo) &&
    input.scanCode.trim().length > 0 &&
    input.scanCode.trim() !== input.serialNo.trim()
  ) {
    return "scan_in_serial";
  }
  if (!(Number(input.qty) > 0)) return "qty_required";
  if (input.uom.trim().length === 0) return "uom_required";
  return undefined;
}

/** The RETURN, no-scan arm (D-081) — see `materialScanReturnOp`. */
export function materialManualReturnOp(
  ctx: TapContext,
  opId: string,
  input: { componentProductId: string; qty: string; uom: string },
): ProductionEventOp {
  return buildEvent(ctx, "material_issued", opId, {
    component_product_id: input.componentProductId,
    qty: negateQty(input.qty),
    uom: input.uom,
  });
}

// ---------------------------------------------------------------------------
// Reading the outbox honestly (the D-080 settlement contract, client side)
// ---------------------------------------------------------------------------

/** The persisted record state the settle fix adds (D-080): a `conflicted`
 * server verdict leaves the send queue and becomes a needs_review record with
 * the server's reason. Widened through a string compare because this half of
 * the slice compiles before floor-outbox's union gains the member. */
export const NEEDS_REVIEW_STATE = "needs_review";

export function isNeedsReview(item: Pick<OutboxItem, "state">): boolean {
  return (item.state as string) === NEEDS_REVIEW_STATE;
}

export function needsReviewItems(items: readonly OutboxItem[]): OutboxItem[] {
  return items.filter((item) => isNeedsReview(item));
}

function isProductionEventOp(op: unknown): op is ProductionEventOp {
  return (
    op !== null && typeof op === "object" && (op as { kind?: unknown }).kind === "production_event"
  );
}

/** Queued-but-unsent production events for ONE operation — what lets the run
 * screen say "2 taps for this op are still on this device" and lets the §3.2
 * complete-guard hint see a qty report that has not reached the server yet. */
export function pendingEventsForOp(
  items: readonly OutboxItem[],
  opId: string,
  eventType?: ProductionEventType,
): OutboxItem[] {
  return items.filter((item) => {
    if (item.state !== "pending") return false;
    const op: unknown = item.op;
    if (!isProductionEventOp(op)) return false;
    if (op.event.wo_operation_id !== opId) return false;
    return eventType === undefined || op.event.event_type === eventType;
  });
}

/**
 * Downtime this DEVICE opened that the server has not confirmed yet, as an
 * `open_downtime[]` row the panel can render and end.
 *
 * The e15 snapshot only knows about stoppages the server has seen. Offline, it
 * knows about none — so the End control was rendered from the snapshot alone
 * and a stoppage started in airplane mode had no way to be closed until the
 * device synced. The operator stands at a stopped machine with no End button,
 * and the run books every minute until the queue drains: minutes that never
 * happened, on the one screen P2's done-gate is about ("a full factory day,
 * OFFLINE, from a phone").
 *
 * `downtimeEndedOp` has always taken the OPENING TAP's `client_event_id`
 * rather than a `downtime_log_id` — its own doc says "from the e15 snapshot's
 * `open_downtime[]`, or this device's own outbox record" — precisely so this
 * would work without a server round trip. The second half was never wired.
 *
 * A start is OPEN when no queued `downtime_ended` names it. Only pending items
 * count on both sides: once a start has drained the snapshot owns it, and once
 * an end has drained the stoppage is closed on the server. `downtime_log_id`
 * is the opening tap's own key, which is unused by the end path and is the
 * only stable id that exists before the server has minted a row.
 */
export function pendingOpenDowntimeForOp(
  items: readonly OutboxItem[],
  opId: string,
  /** Injected so the derivation stays pure and testable; defaults to the
   * item's own tap time, which is when the machine actually stopped. */
  startedAtIso: (item: OutboxItem) => string = (item) => new Date(item.createdAt).toISOString(),
): FloorOpenDowntime[] {
  const ended = new Set(
    items
      .filter((item) => item.state === "pending")
      .map((item) => (isProductionEventOp(item.op) ? item.op.event : null))
      .filter((event) => event?.event_type === "downtime_ended")
      .map((event) => {
        const ref: unknown = (event?.payload as { started_client_event_id?: unknown } | undefined)
          ?.started_client_event_id;
        return typeof ref === "string" ? ref : "";
      })
      .filter((ref) => ref !== ""),
  );
  return pendingEventsForOp(items, opId, "downtime_started")
    .filter((item) => !ended.has(item.clientKey))
    .map((item) => {
      const event = isProductionEventOp(item.op) ? item.op.event : null;
      const reason: unknown = (event?.payload as { downtime_reason_code?: unknown } | undefined)
        ?.downtime_reason_code;
      return {
        // No server row exists yet; the opening tap's own key IS the identity
        // the end path uses, so it stands in for both.
        downtime_log_id: item.clientKey,
        downtime_reason_id: item.clientKey,
        reason_code: typeof reason === "string" ? reason : "",
        started_at: startedAtIso(item),
        wo_operation_id: opId,
        started_client_event_id: item.clientKey,
      };
    });
}

/** The §3.2 operation statuses, as the wire serves them (e14/e15). */
export type OpStatus = StationQueueOperation["status"];

/** §3.2's machine, as a FOLD step: what each floor tap makes the op's status
 * on the device that tapped it. `qty_reported` / `material_issued` change no
 * status — absent here on purpose, so they fold to "no change". */
const STATUS_FOLD: Partial<Record<ProductionEventType, OpStatus>> = {
  op_started: "in_progress",
  op_paused: "paused",
  op_resumed: "in_progress",
  op_completed: "completed",
};

/**
 * The DEVICE's view of an operation's status (§3.4's offline-first contract):
 * the e15 snapshot status with this device's own still-pending taps folded
 * over it, in tap (seq) order. Offline, a worker who tapped Start must see
 * `in_progress` — the queued `op_started` is the device's truth until the
 * server says otherwise — or the Start → qty → Complete chain dead-ends on a
 * stale snapshot.
 *
 * Two hard rules, both the helper's own law rather than the caller's
 * diligence:
 *  - Only `state === "pending"` records fold. A `needs_review` record is an
 *    event the server REFUSED (D-080) — it did not happen, and folding it
 *    would show a state the ledger will never hold. A parked
 *    `needs_attention` record is awaiting the operator in the tray, not
 *    certainly on its way, so it does not fold either.
 *  - The fold does NOT re-validate against §3.2's transition table. It
 *    replays what was tapped, in order — the server is the validator, and an
 *    out-of-order tap will land in conflicts there (§3.4), at which point it
 *    leaves `pending` and stops folding here.
 *
 * Callers pass this op's records (`pendingEventsForOp(items, opId)`); items
 * of other kinds or ops must be filtered out by the caller's scoping.
 */
export function effectiveOpStatus(
  snapshotStatus: OpStatus,
  itemsForOp: readonly Pick<OutboxItem, "state" | "seq" | "op">[],
): OpStatus {
  let status = snapshotStatus;
  const taps = itemsForOp.filter((item) => item.state === "pending").sort((a, b) => a.seq - b.seq);
  for (const item of taps) {
    const op: unknown = item.op;
    if (!isProductionEventOp(op)) continue;
    const next = STATUS_FOLD[op.event.event_type];
    if (next !== undefined) status = next;
  }
  return status;
}

/**
 * What a tap SETTLED as, normalised for a screen. `conflicted` is the D-080
 * outcome the settle fix adds to `OutboxSettlement`; it is read through a
 * narrowing cast for the same compile-ordering reason as `NEEDS_REVIEW_STATE`
 * — and an UNKNOWN future outcome maps to `needs_attention`, never silently
 * to success (the whole class of defect this slice exists to close).
 */
export type TapOutcome =
  | { readonly kind: "applied" }
  | { readonly kind: "queued" }
  | { readonly kind: "conflicted"; readonly reason: string }
  | { readonly kind: "needs_attention"; readonly message: string }
  | { readonly kind: "superseded" }
  | { readonly kind: "not_saved" };

export function tapOutcomeOf(settlement: OutboxSettlement | undefined): TapOutcome {
  if (settlement === undefined) return { kind: "not_saved" };
  const outcome = (settlement as { outcome: string }).outcome;
  switch (outcome) {
    case "applied":
      return { kind: "applied" };
    case "queued":
      return { kind: "queued" };
    case "superseded":
      return { kind: "superseded" };
    case "conflicted": {
      const reason = (settlement as { reason?: unknown }).reason;
      return { kind: "conflicted", reason: typeof reason === "string" ? reason : "" };
    }
    case "needs_attention": {
      const failure = (settlement as { failure?: { message?: unknown } }).failure;
      const message = failure?.message;
      return { kind: "needs_attention", message: typeof message === "string" ? message : "" };
    }
    default:
      return { kind: "needs_attention", message: "" };
  }
}

// ---------------------------------------------------------------------------
// Station + device pins (device state — deliberately OUTSIDE the read cache,
// so a sign-out hand-over keeps the tablet bolted to its station while the
// cached snapshot, and with it the previous worker's id, is purged)
// ---------------------------------------------------------------------------

const STATION_PIN_KEY = "assessly.floor.production.station";
const DEVICE_PIN_KEY = "assessly.floor.production.device";

export interface StationPin {
  readonly workCenterId: string;
  /** Known when pinned from the picker; the /q/wc route pins the id alone and
   * the name fills in from the next successful reference read. */
  readonly name?: string;
}

export interface DevicePin {
  readonly id: string;
  readonly code: string;
  readonly name: string;
}

function safeStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

function readJson(key: string): unknown {
  const storage = safeStorage();
  if (storage === undefined) return undefined;
  try {
    const raw = storage.getItem(key);
    if (raw === null) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return parsed === null || typeof parsed !== "object" ? undefined : parsed;
  } catch {
    return undefined;
  }
}

function writeJson(key: string, value: unknown): void {
  const storage = safeStorage();
  if (storage === undefined) return;
  try {
    if (value === undefined) storage.removeItem(key);
    else storage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota/private mode: the pin simply does not survive a reload.
  }
}

export function pinnedStation(): StationPin | undefined {
  const pin = readJson(STATION_PIN_KEY) as StationPin | undefined;
  return pin !== undefined && typeof pin.workCenterId === "string" && pin.workCenterId.length > 0
    ? pin
    : undefined;
}

/**
 * Pin the station. MERGES the name when one is already known for the same work
 * centre (D-119).
 *
 * The QR route and the station screen both pin the id ALONE — they only have an
 * id — and a plain write therefore DESTROYED the name a picker visit had
 * established: pick "Twister 1", then scan the QR bolted to that same machine,
 * and the screen re-titles itself "Station". The scan is the production path,
 * so the one route an operator actually uses was the one route that could not
 * name the machine. Re-pinning the SAME work centre is a re-assertion of where
 * the tablet is, never new evidence that the machine has no name.
 */
export function pinStation(pin: StationPin): void {
  const previous = pinnedStation();
  const inherited =
    pin.name === undefined && previous?.workCenterId === pin.workCenterId
      ? previous.name
      : pin.name;
  writeJson(STATION_PIN_KEY, {
    workCenterId: pin.workCenterId,
    ...(inherited === undefined ? {} : { name: inherited }),
  });
}

export function clearStationPin(): void {
  writeJson(STATION_PIN_KEY, undefined);
}

export function pinnedDevice(): DevicePin | undefined {
  const pin = readJson(DEVICE_PIN_KEY) as DevicePin | undefined;
  return pin !== undefined && typeof pin.id === "string" && pin.id.length > 0 ? pin : undefined;
}

export function pinDevice(pin: DevicePin): void {
  writeJson(DEVICE_PIN_KEY, pin);
}

export function clearDevicePin(): void {
  writeJson(DEVICE_PIN_KEY, undefined);
}

/**
 * Which registered device THIS tablet is, given the snapshot's registry rows.
 * A persisted pick that is still in the (active) list wins; with EXACTLY ONE
 * active device the answer is unambiguous and auto-picks (the §5.1
 * one-machine auto-assign shape); otherwise the operator must choose — an
 * ambiguous guess would stamp another tablet's identity on every event.
 */
export function resolveDevicePin(
  // `undefined` admitted deliberately: a STALE cached snapshot predating the
  // §5 r15 amendment has no `devices` key, and the defense belongs here, not
  // in every caller.
  devices: readonly StationSnapshotDevice[] | undefined,
  persisted: DevicePin | undefined = pinnedDevice(),
): DevicePin | undefined {
  const list = devices ?? [];
  if (persisted !== undefined && list.some((d) => d.id === persisted.id)) return persisted;
  const only = list.length === 1 ? list[0] : undefined;
  if (only !== undefined) return { id: only.id, code: only.device_code, name: only.name };
  return undefined;
}

// ---------------------------------------------------------------------------
// Scan recognition for the picker input (SEAM: scan.ts, other sub-agent)
// ---------------------------------------------------------------------------
//
// The canonical scan paths are module 02's `/q/wc/{work_center_id}` (station
// kit) and §5 e29's `/q/op/{wo_operation_id}` (traveler per-op QR). scan.ts's
// wc recognition — the settle-fix sub-agent's file — is the eventual owner of
// this grammar; this local parser exists so the PICKER's keyboard-wedge input
// works in this half of the slice, and it must be replaced by an import when
// scan.ts lands the recogniser. Same tolerance rules as scan.ts's product
// parser: full URL, bare path, trailing slash/query, case-insensitive uuid.

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const WC_RE = new RegExp(`/q/wc/(${UUID})`, "i");
const OP_RE = new RegExp(`/q/op/(${UUID})`, "i");
const BARE_RE = new RegExp(`^${UUID}$`, "i");

export type ProductionScan =
  { readonly kind: "wc"; readonly id: string } | { readonly kind: "op"; readonly id: string };

export function parseProductionScan(raw: string): ProductionScan | undefined {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return undefined;
  const wc = WC_RE.exec(trimmed);
  if (wc?.[1] !== undefined) return { kind: "wc", id: wc[1].toLowerCase() };
  const op = OP_RE.exec(trimmed);
  if (op?.[1] !== undefined) return { kind: "op", id: op[1].toLowerCase() };
  // A bare uuid at the STATION picker is a station id — the picker is the only
  // caller, and its question is "which station".
  if (BARE_RE.test(trimmed)) return { kind: "wc", id: trimmed.toLowerCase() };
  return undefined;
}

// ---------------------------------------------------------------------------
// Small pure display helpers
// ---------------------------------------------------------------------------

/** Resolve a `name_i18n` map for the ACTIVE locale, `en` then code fallback —
 * the scrap-reason grid's labels (module 05 causes, localized). */
export function localizedName(
  name: Readonly<Record<string, string | undefined>> | undefined,
  locale: string,
  fallback: string,
): string {
  if (name === undefined) return fallback;
  const short = locale.split("-")[0] ?? locale;
  return name[short] ?? name["en"] ?? fallback;
}

/** Whole minutes until the shift window closes (clamped at 0). */
export function shiftMinutesLeft(shift: Pick<StationShift, "ended_at">, nowMs: number): number {
  const end = Date.parse(shift.ended_at);
  if (Number.isNaN(end)) return 0;
  return Math.max(0, Math.floor((end - nowMs) / 60_000));
}

/** The §2.4 mirror the qty form enforces before enqueueing: a report must
 * report something, and scrap demands a cause (§3.3 step 6). */
/**
 * `defect_handoff_flagged` — §3.3 step 10, slice 04-C-5.
 *
 * WHAT THE DEVICE MAY SAY, AND WHAT IT MAY NOT. The tap carries the quantity,
 * the module-05 cause code, the evidence photo ids and — when the worker knows
 * which step made the defect — the PRODUCING OPERATION. It does NOT carry the
 * producing SHIFT or the producing WORKER, and the omission is the point: those
 * decide whose scoreboard takes the hit, the server resolves them by walking
 * §2.4's timeline backwards from the flag (§3.3 step 10), and a tablet that
 * could name the shift it blamed would BE the gaming vector §2.11 closes.
 * §5.1's "server-resolved fields are NEVER on the wire" is doing real work
 * here rather than tidying a payload.
 *
 * PHOTOS ARE IDS, NOT BYTES. The upload is its own three-call flow
 * (`POST /v1/files` → presigned PUT → store) and it runs FIRST; only the
 * resulting ids ride this event (§3.4). The server refuses an id that is not a
 * STORED `production_evidence` file, so a half-finished upload conflicts rather
 * than writing a row whose evidence panel opens onto nothing.
 */
export function defectHandoffFlaggedOp(
  ctx: TapContext,
  opId: string,
  input: {
    qtyDefective: string;
    scrapReasonCode: string;
    photoFileIds?: readonly string[] | undefined;
    producedWoOperationId?: string | undefined;
    notes?: string | undefined;
  },
): ProductionEventOp {
  return buildEvent(ctx, "defect_handoff_flagged", opId, {
    qty_defective: input.qtyDefective,
    scrap_reason_code: input.scrapReasonCode,
    photo_file_ids: [...(input.photoFileIds ?? [])],
    ...(input.producedWoOperationId !== undefined
      ? { produced_wo_operation_id: input.producedWoOperationId }
      : {}),
    ...(input.notes !== undefined && input.notes !== "" ? { notes: input.notes } : {}),
  });
}

/**
 * The two client-side refusals for a defect flag, mirroring the SERVER's rules
 * exactly and no further: §2.11 makes `qty_defective > 0` a CHECK and
 * `scrap_reason_code` NOT NULL. Both are met at the machine, in the operator's
 * own language, rather than as a conflict row read hours later by somebody in
 * an office — which for an offline device is the difference between a fixable
 * mistake and a lost fact.
 */
export function defectFlagProblem(
  qtyDefective: string,
  scrapReasonCode: string | undefined,
): "nothing_flagged" | "reason_required" | undefined {
  if (!(Number(qtyDefective || "0") > 0)) return "nothing_flagged";
  if (scrapReasonCode === undefined || scrapReasonCode.length === 0) return "reason_required";
  return undefined;
}

export function qtyReportProblem(
  good: string,
  scrap: string,
  scrapReasonCode: string | undefined,
): "nothing_reported" | "scrap_reason_required" | undefined {
  const g = Number(good || "0");
  const s = Number(scrap || "0");
  if (!(g > 0) && !(s > 0)) return "nothing_reported";
  if (s > 0 && (scrapReasonCode === undefined || scrapReasonCode.length === 0)) {
    return "scrap_reason_required";
  }
  return undefined;
}
